import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import HomeIcon from '/icons/home.svg';
import InfoIcon from '/icons/info.svg';
import ChickenIcon from '/icons/chicken.svg';
import BeefIcon from '/icons/beef.svg';
import PigIcon from '/icons/pig.svg';
import FishIcon from '/icons/fish.svg';
import RecipesIcon from '/icons/recipes.svg';
import HalalIcon from '/icons/halal.svg';
import HomeSelectedIcon from '/icons/home_selected.svg';
import InfoSelectedIcon from '/icons/info_selected.svg';
import ChickenSelectedIcon from '/icons/chicken_selected.svg';
import BeefSelectedIcon from '/icons/beef_selected.svg';
import PigSelectedIcon from '/icons/pig_selected.svg';
import FishSelectedIcon from '/icons/fish_selected.svg';
import RecipesSelectedIcon from '/icons/recipes_selected.svg';
import HalalSelectedIcon from '/icons/halal_selected.svg';
import ContactIcon from '/icons/contact.svg';
import ContactSelectedIcon from '/icons/contact_selected.svg';
import { useAuth0 } from '@auth0/auth0-react';

interface NavbarProps {
  activePage: string;
  onPageChange: (pageId: string) => void;
}

const Navbar = ({ activePage, onPageChange }: NavbarProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { loginWithRedirect, isAuthenticated, isLoading, user } = useAuth0();

  // Add debug logs
  console.log('Navbar - Auth State:', { isAuthenticated, isLoading, user });

  const menuItems = [
    { id: 'home', icon: HomeIcon, selectedIcon: HomeSelectedIcon, label: 'Home', path: '/' },
    { id: 'about', icon: InfoIcon, selectedIcon: InfoSelectedIcon, label: 'About', path: '/about' },
    { id: 'beef', icon: BeefIcon, selectedIcon: BeefSelectedIcon, label: 'Beef', path: '/beef' },
    { id: 'chicken', icon: ChickenIcon, selectedIcon: ChickenSelectedIcon, label: 'Chicken', path: '/chicken' },
    { id: 'pork', icon: PigIcon, selectedIcon: PigSelectedIcon, label: 'Pork', path: '/pork' },
    { id: 'fish', icon: FishIcon, selectedIcon: FishSelectedIcon, label: 'Fish', path: '/fish' },
    { id: 'halal', icon: HalalIcon, selectedIcon: HalalSelectedIcon, label: 'Halal', path: '/halal' },
    { id: 'recipes', icon: RecipesIcon, selectedIcon: RecipesSelectedIcon, label: 'Recipes', path: '/recipes' },
    { id: 'contact', icon: ContactIcon, selectedIcon: ContactSelectedIcon, label: 'Contact Us', path: '/contact' },
  ];

  useEffect(() => {
    const currentPath = location.pathname;
    const currentItem = menuItems.find(item => item.path === currentPath);
    if (currentItem) {
      onPageChange(currentItem.id);
    }
  }, [location, onPageChange, menuItems]);

  const handleMemberClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('Member Click - Auth State:', { isAuthenticated, isLoading });
    
    if (isAuthenticated) {
      console.log('Already authenticated, navigating to /member');
      navigate('/member');
    } else {
      console.log('Not authenticated, starting login');
      try {
        await loginWithRedirect({
          appState: { returnTo: '/member' },
          authorizationParams: {
            screen_hint: 'signup',
          },
        });
      } catch (error) {
        console.error('Login error:', error);
      }
    }
  };

  return (
    <header className="bg-black shadow-md fixed top-0 left-0 right-0 z-[60]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-[60]">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/logo.png?id1" 
              alt="Québec Alimentation Logo" 
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6">
            {menuItems.map(({ id, icon: Icon, selectedIcon, label, path }) => (
              <Link
                key={id}
                to={path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex flex-col items-center px-3 py-2 rounded-lg transition-colors ${
                  activePage === id
                    ? 'text-[#e81217]'
                    : 'text-white hover:bg-gray-900 hover:text-[#e81217]'
                }`}
              >
                <img src={activePage === id ? selectedIcon : Icon} alt={label} className="w-5 h-5 mb-1" />
                <span className="text-xs font-medium">{label}</span>
              </Link>
            ))}
          </nav>

          {/* Action Buttons - Desktop */}
          <div className="hidden md:flex items-center space-x-4">
            <button
              onClick={handleMemberClick}
              className="bg-[#e81217] text-white px-6 py-2.5 rounded-full transition-all duration-200 text-base font-bold 
                hover:bg-[#d11015] hover:shadow-lg active:transform active:scale-95 flex items-center space-x-2"
            >
              <span>Espace client</span>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                className="w-5 h-5"
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-white hover:text-custom-red hover:bg-gray-900 focus:outline-none"
            aria-expanded={isMobileMenuOpen}
          >
            <span className="sr-only">Open main menu</span>
            {isMobileMenuOpen ? (
              <img
                src="/icons/close.svg"
                alt="Close menu"
                className="block h-6 w-6"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMobileMenuOpen(false);
                }}
              />
            ) : (
              <img src="/icons/menu.svg" alt="Open menu" className="block h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Container */}
      <div className={`md:hidden ${isMobileMenuOpen ? 'fixed inset-0 z-50 bg-black/80' : 'hidden'}`}>
        <div className="fixed top-24 inset-x-0 bottom-0 bg-black overflow-y-auto z-[55]">
          <div className="px-4 pt-2 pb-4">
            <nav className="mb-6">
              {menuItems.map(({ id, icon: Icon, selectedIcon, label, path }) => (
                <Link
                  key={id}
                  to={path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg transition-colors ${
                    activePage === id
                      ? 'text-[#e81217]'
                      : 'text-white hover:bg-gray-900 hover:text-[#e81217]'
                  }`}
                >
                  <img src={activePage === id ? selectedIcon : Icon} alt={label} className="w-5 h-5" />
                  <span className="text-sm font-medium">{label}</span>
                </Link>
              ))}
            </nav>
            
            {/* Mobile Action Buttons */}
            <div className="space-y-3 px-4 mt-4">
              <button
                onClick={handleMemberClick}
                className="w-full bg-[#e81217] text-white px-6 py-3 rounded-full transition-all duration-200 
                  text-base font-bold hover:bg-[#d11015] active:transform active:scale-95 flex items-center 
                  justify-center space-x-2"
              >
                <span>Espace client</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  className="w-5 h-5"
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                >
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
